import {
  first_value,
  title_case,
  get_verification_status_tone,
  get_verification_impact,
  format_log_timestamp,
  get_case_identifier,
  get_case_name
} from './onboarding_verification_common_helpers';

function get_active_manual_bindings(manual_bindings = []) {
  const active_bindings = (Array.isArray(manual_bindings) ? manual_bindings : [])
    .filter(function active_binding(binding) {
      return binding?.is_active !== false;
    });

  return active_bindings;
}

function get_manual_binding_type(binding = {}) {
  return String(binding?.verification_type || '').trim();
}

function build_manual_binding_entry(binding) {
  return [get_manual_binding_type(binding).toLowerCase(), binding];
}

function has_verification_type(entry) {
  return Boolean(entry[0]);
}

function build_manual_binding_by_type(manual_bindings = []) {
  const binding_entries = get_active_manual_bindings(manual_bindings)
    .map(build_manual_binding_entry)
    .filter(has_verification_type);

  return new Map(binding_entries);
}

function add_manual_history_row(history_by_type, row, index) {
  const verification_type = String(row?.verification_type || '').trim();
  const key = verification_type || `history-${index}`;
  const existing_row = history_by_type.get(key);
  const next_row = pick_latest_manual_history_row(existing_row, row);
  history_by_type.set(key, next_row);
}

function get_manual_history_row_rank(row = {}) {
  const run_id = Number(row?.run_id || row?.id || 0);
  if (run_id > 0) return run_id;

  const timestamp = Date.parse(String(row?.started_at || row?.completed_at || ''));
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function pick_latest_manual_history_row(existing_row = null, next_row = null) {
  if (!existing_row) return next_row;
  if (!next_row) return existing_row;

  return get_manual_history_row_rank(next_row) >= get_manual_history_row_rank(existing_row)
    ? next_row
    : existing_row;
}

function collect_manual_history_by_type(manual_history = []) {
  const history_by_type = new Map();
  const rows = Array.isArray(manual_history) ? manual_history : [];

  rows.forEach(function add_history(row, index) {
    add_manual_history_row(history_by_type, row, index);
  });

  return history_by_type;
}

function normalize_verification_label(verification_type) {
  return title_case(String(verification_type).replace(/[_-]+/g, ' ')) || 'Manual verification';
}

function get_manual_snapshot_run_id(manual = {}) {
  return manual?.run_id || 'latest';
}

function get_manual_history_run_id(row = {}) {
  return row?.run_id || '';
}

function get_manual_impact(row, tone) {
  return row?.overdue_manual ? 'Overdue review' : get_verification_impact(tone);
}

function get_manual_row_status(row = {}) {
  return first_value([
    row?.decision_status,
    row?.status,
    row?.manual?.decision_status,
    row?.manual?.status
  ], 'Pending');
}

function build_manual_snapshot_check(case_item, verification_type, row = {}) {
  const manual = row?.manual;
  const resolved_status = get_manual_row_status(row);
  const tone = get_verification_status_tone(resolved_status);
  const case_identifier = get_case_identifier(case_item);

  return {
    id: `${case_identifier}-manual-${verification_type}-${get_manual_snapshot_run_id(manual)}`,
    check: normalize_verification_label(verification_type),
    case_name: get_case_name(case_item),
    source: 'Manual verification',
    status: title_case(resolved_status),
    status_tone: tone,
    impact: get_manual_impact(row, tone),
    last_run: format_log_timestamp(manual?.completed_at || manual?.started_at),
    application_form_id: null,
    service_id: null,
    case_item,
    verification_mode: 'manual',
    verification_type,
    run_id: first_value([manual?.run_id, manual?.id], null),
    manual_run: manual,
    raw_status: row
  };
}

function build_manual_history_check(case_item, verification_type, row = {}) {
  const resolved_status = get_manual_row_status(row);
  const tone = get_verification_status_tone(resolved_status);
  const case_identifier = get_case_identifier(case_item);

  return {
    id: `${case_identifier}-manual-history-${verification_type}-${get_manual_history_run_id(row)}`,
    check: normalize_verification_label(verification_type),
    case_name: get_case_name(case_item),
    source: 'Manual verification',
    status: title_case(resolved_status),
    status_tone: tone,
    impact: get_verification_impact(tone),
    last_run: format_log_timestamp(row?.completed_at || row?.started_at),
    application_form_id: null,
    service_id: null,
    case_item,
    verification_mode: 'manual',
    verification_type,
    run_id: first_value([row?.run_id, row?.id], null),
    manual_run: row,
    raw_status: row
  };
}

function get_manual_mappings_for_binding(manual_mapping_by_link = {}, binding = {}) {
  const link_id = first_value([
    binding?.manual_form_link_id,
    binding?.manual_form_link,
    binding?.form_link_id,
    binding?.form_link
  ], null);
  const mappings = Reflect.get(manual_mapping_by_link || {}, String(link_id)) || [];

  return mappings;
}

function enrich_existing_manual_check(existing_check, binding, mappings) {
  existing_check.manual_binding = binding;
  existing_check.manual_mappings = mappings;
  existing_check.check = first_value([binding?.manual_verification_form_name, binding?.verification_form_name, existing_check.check], existing_check.check);
  existing_check.source = first_value([binding?.manual_verification_form_name, binding?.verification_form_name, existing_check.source], existing_check.source);
}

function build_configured_manual_check({
  case_item,
  binding,
  mappings,
  verification_type
}) {
  const case_identifier = get_case_identifier(case_item);

  return {
    id: `${case_identifier}-manual-configured-${binding?.id || verification_type}`,
    check: first_value([
      binding?.manual_verification_form_name,
      binding?.verification_form_name,
      normalize_verification_label(verification_type)
    ], 'Manual verification'),
    case_name: get_case_name(case_item),
    source: binding?.manual_verification_form_name || binding?.verification_form_name || 'Configured manual verification',
    status: 'Pending',
    status_tone: 'warning',
    impact: 'Needs manual review',
    last_run: '-',
    application_form_id: null,
    service_id: null,
    case_item,
    verification_mode: 'manual',
    verification_type,
    run_id: null,
    manual_binding: binding,
    manual_mappings: mappings,
    raw_status: null
  };
}

function append_configured_manual_check(context, binding) {
  const verification_type = get_manual_binding_type(binding);
  if (!verification_type) return;

  const mappings = get_manual_mappings_for_binding(context.manual_mapping_by_link, binding);
  const existing_check = context.existing_by_type.get(verification_type.toLowerCase());
  if (existing_check) {
    enrich_existing_manual_check(existing_check, binding, mappings);
    return;
  }

  context.checks.push(build_configured_manual_check({
    case_item: context.case_item,
    binding,
    mappings,
    verification_type
  }));
}

function snapshot_has_manual_row(row) {
  return Boolean(row?.manual);
}

function binding_exists(binding_by_type, verification_type) {
  return binding_by_type.has(String(verification_type).toLowerCase());
}

function append_snapshot_check(context, entry) {
  const verification_type = entry[0];
  const row = entry[1];

  if (!snapshot_has_manual_row(row) || !binding_exists(context.manual_binding_by_type, verification_type)) {
    return;
  }

  context.checks.push(build_manual_snapshot_check(context.case_item, verification_type, row));
}

function append_history_check(context, row, verification_type) {
  const has_snapshot = Boolean(context.snapshot_by_type.get(verification_type)?.manual);

  if (has_snapshot || !binding_exists(context.manual_binding_by_type, verification_type)) {
    return;
  }

  context.checks.push(build_manual_history_check(context.case_item, verification_type, row));
}

function build_existing_by_type(checks) {
  const entries = checks.map(function existing_entry(check) {
    return [String(check?.verification_type || '').trim().toLowerCase(), check];
  });

  return new Map(entries);
}

function build_manual_context({
  case_item,
  manual_status_snapshot = {},
  manual_history = [],
  manual_bindings = [],
  manual_mapping_by_link = {}
}) {
  const snapshot = manual_status_snapshot && typeof manual_status_snapshot === 'object'
    ? manual_status_snapshot
    : {};
  const active_manual_bindings = get_active_manual_bindings(manual_bindings);

  return {
    checks: [],
    snapshot,
    snapshot_by_type: new Map(Object.entries(snapshot)),
    active_manual_bindings,
    history_by_type: collect_manual_history_by_type(manual_history),
    manual_binding_by_type: build_manual_binding_by_type(active_manual_bindings),
    manual_mapping_by_link,
    case_item
  };
}

export function build_manual_checks(options) {
  const context = build_manual_context(options);

  Object.entries(context.snapshot).forEach(function add_snapshot(entry) {
    append_snapshot_check(context, entry);
  });

  context.history_by_type.forEach(function add_history(row, verification_type) {
    append_history_check(context, row, verification_type);
  });

  context.existing_by_type = build_existing_by_type(context.checks);
  context.active_manual_bindings.forEach(function add_configured(binding) {
    append_configured_manual_check(context, binding);
  });

  return context.checks;
}
